import { useState, useMemo, useCallback } from "react";
import { useAction } from "convex/react";
import { SharePointContextMenu, type ContextMenuItemDef } from "./ContextMenu";
import { testIds } from "../../lib/testIds";
import { useRepancyDocuments } from "../../lib/showcase/sharePointHooks";
import { useDocumentWindow } from "../../hooks/useDocumentWindow";
import { isRealSharePointWebUrl } from "./DocumentPreview";
import { useResolvedPortalSlug } from "../../lib/tenant/portalSlugFromUrl";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { HelpTip } from "../ui/HelpTip";
import { useAppDispatch } from "../../store";
import { useWindowContext } from "../desktop/WindowContext";
import {
  dispatchPromptLabPrefill,
  PROMPT_LAB_PATH,
} from "../../lib/promptLab/sendDocToPromptLab";
import {
  DocumentType,
  ConfidentialityLevel,
} from "@repancy/core";

// ---------------------------------------------------------------------------
// Mock data (documents with full metadata)
// ---------------------------------------------------------------------------

interface MockDocument {
  _id: string;
  matterId: string;
  title: string;
  documentType: string;
  confidentialityLevel: string;
  responsibleAttorneyId: string;
  currentVersion: number;
  modifiedAt: string;
  content: string;
  // K.3.a — surface the real SharePoint URL on each row when the live
  // doc carries one. Undefined for fixture rows.
  sharePointWebUrl?: string;
  // Phase A.1 (2026-05-27) — anonymous Graph share-link URL minted at
  // provision-time. Preferred over `sharePointWebUrl` because the
  // anonymous URL works without tenant membership.
  publicShareUrl?: string;
}

const mockDocuments: MockDocument[] = [
  { _id: "d1", matterId: "m1", title: "Draft Purchase Agreement v3", documentType: "Contract", confidentialityLevel: "AttorneyClientPrivileged", responsibleAttorneyId: "atty-1", currentVersion: 3, modifiedAt: "2026-05-10T14:30:00Z", content: "" },
  { _id: "d2", matterId: "m1", title: "Due Diligence Checklist", documentType: "Memorandum", confidentialityLevel: "Confidential", responsibleAttorneyId: "atty-2", currentVersion: 1, modifiedAt: "2026-05-08T09:15:00Z", content: "" },
  { _id: "d3", matterId: "m1", title: "Target Company Financials Summary", documentType: "Brief", confidentialityLevel: "WorkProduct", responsibleAttorneyId: "atty-1", currentVersion: 2, modifiedAt: "2026-05-05T16:45:00Z", content: "" },
  { _id: "d4", matterId: "m2", title: "Separation Agreement Template", documentType: "Contract", confidentialityLevel: "Confidential", responsibleAttorneyId: "atty-3", currentVersion: 1, modifiedAt: "2026-05-09T11:00:00Z", content: "" },
  { _id: "d5", matterId: "m2", title: "WARN Act Compliance Memo", documentType: "Memorandum", confidentialityLevel: "WorkProduct", responsibleAttorneyId: "atty-2", currentVersion: 2, modifiedAt: "2026-05-07T13:20:00Z", content: "" },
  { _id: "d6", matterId: "m3", title: "Trust Amendment Draft", documentType: "Contract", confidentialityLevel: "AttorneyClientPrivileged", responsibleAttorneyId: "atty-1", currentVersion: 4, modifiedAt: "2026-05-11T10:00:00Z", content: "" },
  { _id: "d7", matterId: "m3", title: "Beneficiary Notification Letter", documentType: "Correspondence", confidentialityLevel: "Confidential", responsibleAttorneyId: "atty-3", currentVersion: 1, modifiedAt: "2026-05-06T08:30:00Z", content: "" },
  { _id: "d8", matterId: "m5", title: "Term Sheet - Series B", documentType: "Contract", confidentialityLevel: "Confidential", responsibleAttorneyId: "atty-2", currentVersion: 5, modifiedAt: "2026-05-12T15:00:00Z", content: "" },
  { _id: "d9", matterId: "m5", title: "Board Resolution", documentType: "Motion", confidentialityLevel: "AttorneyClientPrivileged", responsibleAttorneyId: "atty-1", currentVersion: 1, modifiedAt: "2026-05-10T09:45:00Z", content: "" },
  { _id: "d10", matterId: "m5", title: "Investor Rights Agreement", documentType: "Contract", confidentialityLevel: "Confidential", responsibleAttorneyId: "atty-3", currentVersion: 2, modifiedAt: "2026-05-09T14:10:00Z", content: "" },
  { _id: "d11", matterId: "m9", title: "Limited Partnership Agreement", documentType: "Contract", confidentialityLevel: "AttorneyClientPrivileged", responsibleAttorneyId: "atty-1", currentVersion: 3, modifiedAt: "2026-05-13T11:30:00Z", content: "" },
  { _id: "d12", matterId: "m9", title: "PPM Draft", documentType: "Brief", confidentialityLevel: "Confidential", responsibleAttorneyId: "atty-2", currentVersion: 2, modifiedAt: "2026-05-11T16:00:00Z", content: "" },
  { _id: "d13", matterId: "m10", title: "LOI - Target Acquisition", documentType: "Correspondence", confidentialityLevel: "Confidential", responsibleAttorneyId: "atty-3", currentVersion: 1, modifiedAt: "2026-05-12T10:20:00Z", content: "" },
  { _id: "d14", matterId: "m10", title: "Merger Agreement Draft", documentType: "Contract", confidentialityLevel: "AttorneyClientPrivileged", responsibleAttorneyId: "atty-1", currentVersion: 2, modifiedAt: "2026-05-13T09:00:00Z", content: "" },
  { _id: "d15", matterId: "m10", title: "Antitrust Analysis Memo", documentType: "Memorandum", confidentialityLevel: "WorkProduct", responsibleAttorneyId: "atty-2", currentVersion: 1, modifiedAt: "2026-05-10T12:00:00Z", content: "" },
  { _id: "d16", matterId: "m4", title: "Motion to Compel Discovery", documentType: "Motion", confidentialityLevel: "Public", responsibleAttorneyId: "atty-1", currentVersion: 1, modifiedAt: "2026-05-04T10:00:00Z", content: "" },
  { _id: "d17", matterId: "m6", title: "IP License Agreement Draft", documentType: "Contract", confidentialityLevel: "Confidential", responsibleAttorneyId: "atty-3", currentVersion: 3, modifiedAt: "2026-05-11T14:30:00Z", content: "" },
  { _id: "d18", matterId: "m7", title: "CC&R Amendment Resolution", documentType: "Motion", confidentialityLevel: "Public", responsibleAttorneyId: "atty-2", currentVersion: 2, modifiedAt: "2026-05-08T11:15:00Z", content: "" },
  { _id: "d19", matterId: "m8", title: "Construction Lien Filing", documentType: "Brief", confidentialityLevel: "Public", responsibleAttorneyId: "atty-1", currentVersion: 1, modifiedAt: "2026-05-03T09:00:00Z", content: "" },
];

const ATTORNEY_NAMES: Record<string, string> = {
  "atty-1": "Sarah Chen",
  "atty-2": "Marcus Webb",
  "atty-3": "Diana Lopez",
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DocumentListProps {
  matterId: string | null;
  onSelectDocument: (docId: string) => void;
  selectedDocumentId: string | null;
}

type SortField = "title" | "documentType" | "confidentialityLevel" | "responsibleAttorneyId" | "currentVersion" | "modifiedAt";
type SortDir = "asc" | "desc";

const FONT = "var(--xp-font-body)";

const CONFIDENTIALITY_COLORS: Record<string, string> = {
  Public: "#008000",
  Confidential: "#FF8C00",
  AttorneyClientPrivileged: "#CC0000",
  WorkProduct: "var(--xp-selection)",
};

const DOC_TYPES = ["Contract", "Motion", "Brief", "Memorandum", "Correspondence"];
const CONF_LEVELS = ["Public", "Confidential", "AttorneyClientPrivileged", "WorkProduct"];

// ---------------------------------------------------------------------------
// Column config
// ---------------------------------------------------------------------------

interface ColDef {
  key: SortField;
  label: string;
  width: number | string;
  filterable?: "text" | "select";
  options?: string[];
}

const COLUMNS: ColDef[] = [
  { key: "title", label: "Name", width: "1fr", filterable: "text" },
  { key: "documentType", label: "Type", width: 110, filterable: "select", options: DOC_TYPES },
  { key: "confidentialityLevel", label: "Confidentiality", width: 140, filterable: "select", options: CONF_LEVELS },
  { key: "responsibleAttorneyId", label: "Attorney", width: 120 },
  { key: "currentVersion", label: "Version", width: 60 },
  { key: "modifiedAt", label: "Modified", width: 100 },
];

function gridTemplate() {
  return COLUMNS.map((c) => (typeof c.width === "number" ? `${c.width}px` : c.width)).join(" ");
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function ConfidentialityBadge({ level }: { level: string }) {
  const color = CONFIDENTIALITY_COLORS[level] ?? "#000";
  const label = level === "AttorneyClientPrivileged" ? "Atty-Client" : level;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 6px",
        fontSize: 10,
        fontFamily: FONT,
        fontWeight: "bold",
        color: "white",
        background: color,
        borderRadius: 2,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentList({ matterId, onSelectDocument, selectedDocumentId }: DocumentListProps) {
  // 2026-05-27 — right-click "Send to Prompt Lab" needs both the Redux
  // dispatcher (to prefill the prompt session) and the window context
  // (to open/focus the /prompt-lab window). Same wiring as the button
  // in DocumentPreview's action row.
  const dispatch = useAppDispatch();
  const windowContext = useWindowContext();

  // useRepancyDocuments picks live or demo Convex query based on `?demo=1`.
  // Mock ids (m1, m2…) auto-skip at the hook layer.
  const live = useRepancyDocuments(matterId);
  const allDocs: MockDocument[] = live && live.length > 0
    ? live.map((d) => ({
        _id: d._id as unknown as string,
        matterId: d.matterId as unknown as string,
        title: d.title,
        documentType: d.documentType,
        confidentialityLevel: d.confidentialityLevel,
        responsibleAttorneyId: ('responsibleAttorneyId' in d ? d.responsibleAttorneyId : null) ?? '',
        currentVersion: d.currentVersion,
        modifiedAt: d.modifiedAt,
        content: d.content,
        // K.3.a — preserve the SharePoint webUrl from the live row so the
        // per-row "↗" icon can render. Demo rows + fixture rows skip this.
        sharePointWebUrl: (d as { externalRef?: { webUrl?: string } }).externalRef?.webUrl,
        // Phase A.1 (2026-05-27) — anonymous share-link, preferred for
        // the inline open anchor below.
        publicShareUrl: (d as { publicShareUrl?: string }).publicShareUrl,
      }))
    : mockDocuments;

  // Phase H (H.3) — double-click opens the doc in its own window.
  const { openDocument } = useDocumentWindow();

  const [sortField, setSortField] = useState<SortField>("modifiedAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [filterName, setFilterName] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterConf, setFilterConf] = useState("");
  const [ctxMenu, setCtxMenu] = useState<{ items: ContextMenuItemDef[]; position: { x: number; y: number } } | null>(null);

  const docs = useMemo(() => {
    let list = matterId ? allDocs.filter((d) => d.matterId === matterId) : [];
    if (filterName) list = list.filter((d) => d.title.toLowerCase().includes(filterName.toLowerCase()));
    if (filterType) list = list.filter((d) => d.documentType === filterType);
    if (filterConf) list = list.filter((d) => d.confidentialityLevel === filterConf);

    list.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [allDocs, matterId, sortField, sortDir, filterName, filterType, filterConf]);

  // Phase 6.5 — per-matter public share-link generator. Same UX pattern
  // as the per-doc Share publicly button in DocumentPreview; result
  // panel toggles open under a small matter-action bar above the
  // column headers.
  // 2026-05-28 — URL-fallback slug (registered windows live outside
  // <TenantBrandingProvider> when rendered by the outer WindowLayer).
  const portalSlug = useResolvedPortalSlug();
  const createMatterShareLink = useAction(
    api.sharePointPublicLinks.createMatterShareLink,
  );
  const [matterShareResult, setMatterShareResult] = useState<
    | { kind: 'idle' }
    | { kind: 'pending' }
    | { kind: 'ok'; webUrl: string; expiresAt: string; scope: 'anonymous' | 'organization'; cached?: true }
    | { kind: 'error'; reason: string }
  >({ kind: 'idle' });
  const [matterShareCopied, setMatterShareCopied] = useState(false);

  const handleCreateMatterShare = useCallback(async (): Promise<void> => {
    if (!portalSlug) {
      setMatterShareResult({ kind: 'error', reason: 'No portal context loaded.' });
      return;
    }
    if (!matterId) {
      setMatterShareResult({ kind: 'error', reason: 'No matter selected.' });
      return;
    }
    setMatterShareResult({ kind: 'pending' });
    setMatterShareCopied(false);
    try {
      const r = await createMatterShareLink({
        clientSlug: portalSlug,
        matterId: matterId as Id<'matters'>,
      });
      if (r.ok) {
        setMatterShareResult({
          kind: 'ok',
          webUrl: r.webUrl,
          expiresAt: r.expiresAt,
          scope: r.scope,
          ...(r.cached ? { cached: true as const } : {}),
        });
      } else {
        setMatterShareResult({ kind: 'error', reason: r.reason });
      }
    } catch (err) {
      setMatterShareResult({
        kind: 'error',
        reason: err instanceof Error ? err.message : String(err),
      });
    }
  }, [portalSlug, createMatterShareLink, matterId]);

  const handleCopyMatterShareUrl = useCallback(async (url: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setMatterShareCopied(true);
      setTimeout(() => setMatterShareCopied(false), 2000);
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const handleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return field;
    });
  }, []);

  function handleRowContext(e: React.MouseEvent, doc: MockDocument) {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({
      items: [
        { label: "Open", onClick: () => onSelectDocument(doc._id) },
        {
          label: "Send to Prompt Lab",
          onClick: () => {
            // Same handler shape as DocumentPreview's button — prime
            // a Prompt Lab session with the doc as context, then open
            // + focus the /prompt-lab window. Pass metadata so the
            // Document Context chip resolves (without this, the
            // session lists the id but `s.documents.entities[id]` is
            // empty and the chip silently drops out).
            dispatchPromptLabPrefill(dispatch, {
              docId: doc._id,
              docTitle: doc.title,
              docContent: doc.content,
              matterId: doc.matterId,
              documentType: doc.documentType as DocumentType,
              confidentialityLevel:
                doc.confidentialityLevel as ConfidentialityLevel,
              responsibleAttorneyId: doc.responsibleAttorneyId,
              modifiedAt: doc.modifiedAt,
            });
            const win = windowContext.getWindow(PROMPT_LAB_PATH);
            if (!win) {
              windowContext.openWindow(PROMPT_LAB_PATH);
            } else {
              windowContext.openWindow(PROMPT_LAB_PATH);
              windowContext.focusWindow(PROMPT_LAB_PATH);
            }
          },
        },
        { separator: true, label: "", onClick: () => {} },
        { label: "Version History", onClick: () => {}, disabled: true },
        { label: "Properties", onClick: () => {}, disabled: true },
        { separator: true, label: "", onClick: () => {} },
        { label: "Delete", onClick: () => {}, disabled: true },
      ],
      position: { x: e.clientX, y: e.clientY },
    });
  }

  if (!matterId) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "white",
          borderStyle: "solid",
          borderWidth: 2,
          fontFamily: FONT,
          fontSize: 12,
          color: "#808080",
        }}
      >
        Select a matter from the tree to view documents
      </div>
    );
  }

  const selectStyle: React.CSSProperties = {
    height: 20,
    fontFamily: FONT,
    fontSize: 11,
    border: "1px solid #808080",
    background: "white",
    padding: "0 2px",
    width: "100%",
    boxSizing: "border-box",
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "white",
        borderStyle: "solid",
        borderWidth: 2,
        fontFamily: FONT,
        fontSize: 12,
        overflow: "hidden",
      }}
    >
      {/* Phase 6.5 — Matter action bar with public-share button.
          Appears above the column headers when a matter is selected. */}
      <div
        data-testid={testIds.explorer.shareMatterPublicly + '-bar'}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 6,
          padding: '4px 6px',
          background: '#ece9d8',
          borderBottom: '1px solid #aca899',
          flexShrink: 0,
        }}
      >
        <HelpTip k="explorer.matter.sharePublicly" wrap>
          <button
            type="button"
            data-testid={testIds.explorer.shareMatterPublicly}
            onClick={() => void handleCreateMatterShare()}
            disabled={matterShareResult.kind === 'pending'}
            style={{
              fontFamily: FONT,
              fontSize: 11,
              fontWeight: 'bold',
              padding: '3px 10px',
              background: '#d0d0f0',
              borderStyle: 'solid',
              borderWidth: 2,
              color: '#000080',
              cursor: matterShareResult.kind === 'pending' ? 'wait' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {matterShareResult.kind === 'pending'
              ? 'Creating link…'
              : 'Get share link'}
          </button>
        </HelpTip>
      </div>

      {/* Matter share-link result panel — toggles open after click. */}
      {matterShareResult.kind === 'ok' ? (
        <div
          data-testid={testIds.explorer.shareMatterResultPanel}
          style={{
            padding: '6px 12px',
            background: '#fffbcc',
            borderBottom: '1px solid #b08800',
            fontSize: 11,
            color: '#000',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            flexShrink: 0,
          }}
        >
          <div style={{ fontWeight: 'bold' }}>
            {matterShareResult.cached === true
              ? 'Existing matter link (reused — still valid)'
              : 'Matter public link created'}
            {matterShareResult.scope === 'organization' ? (
              <span style={{ marginLeft: 6, fontWeight: 'normal', color: '#374151' }}>
                — tenant only allows org-scope
              </span>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="text"
              readOnly
              value={matterShareResult.webUrl}
              data-testid={testIds.explorer.shareMatterUrlInput}
              onFocus={(e) => e.currentTarget.select()}
              style={{
                flex: 1,
                fontFamily: 'var(--xp-font-mono)',
                fontSize: 11,
                padding: '3px 6px',
                background: '#fff',
                border: '1px solid #808080',
                color: '#000',
              }}
            />
            <button
              type="button"
              data-testid={testIds.explorer.shareMatterCopyButton}
              onClick={() => void handleCopyMatterShareUrl(matterShareResult.webUrl)}
              style={{
                fontFamily: FONT,
                fontSize: 11,
                padding: '3px 10px',
                background: '#ece9d8',
                borderStyle: 'solid',
                borderWidth: 1,
                color: '#000',
                cursor: 'pointer',
              }}
            >
              {matterShareCopied ? '✓ Copied' : 'Copy link'}
            </button>
            <a
              href={matterShareResult.webUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={testIds.explorer.shareMatterOpenLink}
              style={{
                fontFamily: FONT,
                fontSize: 11,
                padding: '3px 10px',
                background: '#ece9d8',
                borderStyle: 'solid',
                borderWidth: 1,
                color: '#000',
                cursor: 'pointer',
                textDecoration: 'none',
              }}
            >
              Open ↗
            </a>
            <button
              type="button"
              onClick={() => setMatterShareResult({ kind: 'idle' })}
              aria-label="Dismiss"
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: 14,
                lineHeight: 1,
                cursor: 'pointer',
                color: '#000',
                padding: '0 4px',
              }}
            >
              ×
            </button>
          </div>
          <div style={{ fontSize: 10, color: '#374151' }}>
            Expires {new Date(matterShareResult.expiresAt).toLocaleDateString()} ·
            demo workspace, sample data, anyone with this URL can view the matter folder.
          </div>
        </div>
      ) : null}
      {matterShareResult.kind === 'error' ? (
        <div
          data-testid={testIds.explorer.shareMatterErrorPanel}
          style={{
            padding: '6px 12px',
            background: '#ffe8e8',
            borderBottom: '1px solid #cc6666',
            fontSize: 11,
            color: '#660000',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <span>Could not create matter link: {matterShareResult.reason}</span>
          <button
            type="button"
            onClick={() => setMatterShareResult({ kind: 'idle' })}
            style={{
              marginLeft: 'auto',
              border: '1px solid #660000',
              background: 'transparent',
              color: '#660000',
              padding: '0 6px',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* Column headers */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: gridTemplate(),
          background: "#ece9d8",
          borderBottom: "1px solid #808080",
          flexShrink: 0,
        }}
      >
        {COLUMNS.map((col) => (
          <div
            key={col.key}
            data-testid={testIds.explorer.columnHeader(col.key)}
            onClick={() => handleSort(col.key)}
            style={{
              padding: "3px 6px",
              fontWeight: "bold",
              fontSize: 11,
              cursor: "pointer",
              userSelect: "none",
              borderRight: "1px solid #808080",
              borderStyle: "solid",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              whiteSpace: "nowrap",
            }}
          >
            <span>{col.label}</span>
            {sortField === col.key && (
              <span data-testid={testIds.explorer.sortIndicator(col.key)} style={{ fontSize: 8, marginLeft: 4 }}>
                {sortDir === "asc" ? "▲" : "▼"}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Filter row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: gridTemplate(),
          background: "#d9e8fa",
          borderBottom: "1px solid #808080",
          flexShrink: 0,
          padding: "2px 0",
        }}
      >
        {/* Name filter */}
        <div style={{ padding: "0 4px" }}>
          <input
            type="text"
            placeholder="Filter..."
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            data-testid={testIds.explorer.filterInput('name')}
            style={{
              ...selectStyle,
              height: 20,
            }}
          />
        </div>
        {/* Type filter */}
        <div style={{ padding: "0 4px" }}>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid={testIds.explorer.filterInput('type')} style={selectStyle}>
            <option value="">All</option>
            {DOC_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        {/* Confidentiality filter */}
        <div style={{ padding: "0 4px" }}>
          <select value={filterConf} onChange={(e) => setFilterConf(e.target.value)} data-testid={testIds.explorer.filterInput('confidentiality')} style={selectStyle}>
            <option value="">All</option>
            {CONF_LEVELS.map((l) => (
              <option key={l} value={l}>{l === "AttorneyClientPrivileged" ? "Atty-Client" : l}</option>
            ))}
          </select>
        </div>
        {/* Empty cells for non-filterable columns */}
        <div />
        <div />
        <div />
      </div>

      {/* Rows */}
      <div style={{ flex: 1, overflow: "auto" }}>
        {docs.length === 0 ? (
          <div style={{ padding: 12, color: "#808080", textAlign: "center" }}>
            No documents found
          </div>
        ) : (
          docs.map((doc) => {
            const isSelected = selectedDocumentId === doc._id;
            return (
              <div
                key={doc._id}
                data-testid={testIds.explorer.docRow(doc._id)}
                onClick={() => onSelectDocument(doc._id)}
                onDoubleClick={() => {
                  // Phase H (H.3) — open this doc in its own window keyed
                  // by its id, alongside the side-panel selection so the
                  // explorer still highlights the row.
                  onSelectDocument(doc._id);
                  openDocument(doc._id, doc.title);
                }}
                onContextMenu={(e) => handleRowContext(e, doc)}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("application/x-repancy-doc", doc._id);
                  e.dataTransfer.effectAllowed = "copy";
                }}
                style={{
                  display: "grid",
                  gridTemplateColumns: gridTemplate(),
                  height: 24,
                  alignItems: "center",
                  cursor: "pointer",
                  background: isSelected ? "var(--xp-selection)" : "transparent",
                  color: isSelected ? "white" : "#000",
                  borderBottom: "1px solid #d9e8fa",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    (e.currentTarget as HTMLDivElement).style.background = "var(--xp-selection)";
                    (e.currentTarget as HTMLDivElement).style.color = "white";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    (e.currentTarget as HTMLDivElement).style.background = "transparent";
                    (e.currentTarget as HTMLDivElement).style.color = "#000";
                  }
                }}
              >
                <div style={{ padding: "0 6px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {"📄"} {doc.title}
                  </span>
                  {(() => {
                    // Phase A.1 — prefer the anonymous share-link
                    // (works for any visitor); fall back to the raw
                    // driveItem webUrl (tenant-members only).
                    const rowOpenUrl = isRealSharePointWebUrl(doc.publicShareUrl)
                      ? doc.publicShareUrl
                      : doc.sharePointWebUrl;
                    return isRealSharePointWebUrl(rowOpenUrl) ? (
                    <a
                      href={rowOpenUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={testIds.explorer.docRowOpenInSharePoint(doc._id)}
                      title="Open in SharePoint"
                      aria-label={`Open ${doc.title} in SharePoint`}
                      onClick={(e) => {
                        // K.3.a — Don't trigger row selection / double-click
                        // open when clicking the inline anchor.
                        e.stopPropagation();
                      }}
                      style={{
                        fontSize: 11,
                        textDecoration: "none",
                        color: "inherit",
                        flexShrink: 0,
                      }}
                    >
                      ↗
                    </a>
                    ) : null;
                  })()}
                </div>
                <div style={{ padding: "0 6px", fontSize: 11 }}>
                  {doc.documentType}
                </div>
                <div style={{ padding: "0 6px" }}>
                  <ConfidentialityBadge level={doc.confidentialityLevel} />
                </div>
                <div style={{ padding: "0 6px", fontSize: 11 }}>
                  {ATTORNEY_NAMES[doc.responsibleAttorneyId] ?? doc.responsibleAttorneyId}
                </div>
                <div style={{ padding: "0 6px", textAlign: "center", fontSize: 11 }}>
                  v{doc.currentVersion}
                </div>
                <div style={{ padding: "0 6px", fontSize: 11 }}>
                  {formatDate(doc.modifiedAt)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Status bar */}
      <div
        style={{
          height: 22,
          display: "flex",
          alignItems: "center",
          padding: "0 8px",
          background: "#ece9d8",
          borderTop: "1px solid #808080",
          fontSize: 11,
          color: "#000",
          flexShrink: 0,
        }}
      >
        {docs.length} document{docs.length !== 1 ? "s" : ""}
      </div>

      {ctxMenu && (
        <SharePointContextMenu
          items={ctxMenu.items}
          position={ctxMenu.position}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}
